import { ChevronDownIcon, ChevronUpIcon, DragIcon } from '@/assets/icons/icons';
import {
  CustomButton,
  CustomShipmentText,
  ToggleSwitch,
  TruncateWithTooltip,
} from '@/components';
import SearchWithMatchNavigator from '@/components/SearchWithMatchNavigator';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox, Divider, Flex, Tooltip } from 'antd';
import { memo, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { HeaderItem } from '../types';
import { capitalize } from '@/common/helper';
import { trackSearch } from '@/hooks';

type HeaderWithGroup = HeaderItem & {
  group?: string;
  group_name?: string;
};

interface ReportPanelContentProps {
  appliedHeaderList: HeaderItem[];
  sendHeadersToParent: (headers: HeaderItem[]) => void;
  isShipmentColumns?: boolean;
}

const ReportPanelContent: FC<ReportPanelContentProps> = ({
  appliedHeaderList,
  sendHeadersToParent,
  isShipmentColumns = false,
}) => {
  const [headers, setHeaders] = useState<HeaderItem[]>([]);
  const [activeView, setActiveView] = useState<'include' | 'reorder'>(
    'include'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [reorderMatches, setReorderMatches] = useState<string[]>([]);
  const [activeMatchIndex, setActiveMatchIndex] = useState<number>(0);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  useEffect(() => {
    setHeaders(appliedHeaderList);
  }, [appliedHeaderList]);

  // Column search is filtered client-side (headers are already loaded), so
  // there's no network request to hook auditSearch into — track it directly,
  // only when opened from the Shipments page's columns dropdown.
  useEffect(() => {
    if (!isShipmentColumns) return;
    const q = searchQuery.trim();
    if (!q) return;
    trackSearch(q);
  }, [searchQuery, isShipmentColumns]);

  const getGroupKey = (h: HeaderWithGroup | HeaderItem): string => {
    return (
      (h.group && String(h.group).trim()) ||
      (h.group_name && String(h.group_name).trim()) ||
      'Other'
    );
  };

  const { allGroups, groupOrder, groupLabels } = useMemo(() => {
    if (activeView !== 'include') {
      return { allGroups: {}, groupOrder: [], groupLabels: {} };
    }

    const map: Record<string, HeaderWithGroup[]> = {};
    const order: string[] = [];
    const labels: Record<string, string> = {};

    (headers as HeaderWithGroup[])?.forEach((h) => {
      const g = getGroupKey(h);

      if (!map[g]) {
        map[g] = [];
        order.push(g);
        labels[g] =
          (typeof h.group_name === 'string' && h.group_name.trim()) || g;
      }

      map[g].push(h);
    });

    return { allGroups: map, groupOrder: order, groupLabels: labels };
  }, [headers, activeView]);

  const filteredByGroup: Record<string, HeaderWithGroup[]> = useMemo(() => {
    if (activeView !== 'include') return allGroups;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allGroups;

    const result: Record<string, HeaderWithGroup[]> = {};
    Object.entries(allGroups)?.forEach(([g, items]) => {
      const filteredItems = items?.filter((item) => {
        const v = String(item.value ?? '').toLowerCase();
        const k = String(item.key ?? '').toLowerCase();
        return v.includes(q) || k.includes(q);
      });
      if (filteredItems?.length) {
        result[g] = filteredItems;
      }
    });
    return result;
  }, [allGroups, searchQuery, activeView]);

  useEffect(() => {
    if (activeView !== 'include') return;
    if (!groupOrder?.length) {
      setOpenGroups({});
      return;
    }
    setOpenGroups((prev) => {
      const next: Record<string, boolean> = {};
      groupOrder?.forEach((g, i) => {
        next[g] = prev[g] ?? i === 0;
      });
      return next;
    });
  }, [groupOrder, activeView]);

  const toggleGroup = (g: string) =>
    setOpenGroups((s) => ({ ...s, [g]: !s[g] }));

  //   const reorderHeaders: HeaderWithGroup[] = useMemo(() => {
  //   const enabled = appliedHeaderList.filter((h) => h.enable);

  //   if (activeView !== 'reorder') return enabled;

  //   const q = searchQuery.trim().toLowerCase();
  //   if (!q) return enabled;

  //   return enabled.filter((item) => {
  //     const v = String(item.value ?? '').toLowerCase();
  //     const k = String(item.key ?? '').toLowerCase();
  //     return v.includes(q) || k.includes(q);
  //   });
  // }, [activeView, searchQuery, appliedHeaderList]);

  const reorderHeaders: HeaderWithGroup[] = useMemo(() => {
    const enabled = headers?.filter((h) => h?.enable);
    return enabled;
  }, [headers]);

  const reorderItemIds = useMemo(
    () =>
      reorderHeaders.flatMap((item) =>
        typeof item.key === 'string' ? [item.key] : []
      ),
    [reorderHeaders]
  );

  const includedCount = useMemo(
    () => headers?.filter((item) => item.enable).length,
    [headers]
  );

  const showSelectAll = useMemo(
    () =>
      groupOrder?.length > 0 &&
      groupOrder?.every((g) => (filteredByGroup[g] || []).length > 0),
    [groupOrder, filteredByGroup]
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    //    const appliedHeaders = [...headers];
    //   const oldIndex = appliedHeaders.findIndex((item) => item.key === active.id);
    //   const newIndex = appliedHeaders.findIndex((item) => item.key === over.id);
    //   if (oldIndex === -1 || newIndex === -1){
    //    // sendHeadersToParent(appliedHeaders)
    //     return appliedHeaders;
    //   }

    //   const updated = arrayMove(appliedHeaders, oldIndex, newIndex);
    //  // sendHeadersToParent(updated)
    //   return updated;

    setHeaders((prev) => {
      const oldIndex = prev.findIndex((item) => item.key === active.id);
      const newIndex = prev.findIndex((item) => item.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;

      const updated = arrayMove(prev, oldIndex, newIndex);
      return updated;
    });
    // sendHeadersToParent()
  };

  const isAllSelected = useMemo(
    () => headers?.length > 0 && headers.every((h) => h.enable),
    [headers]
  );

  const handleToggleAll = () => {
    const next = !isAllSelected;
    // if (headers?.length > 0) {
    //   const updated = headers?.map((header) => ({ ...header, enable: next }));
    //   sendHeadersToParent(updated);
    //   return updated;
    // }

    setHeaders((prev) => {
      const updated = prev.map((header) => {
        // Do NOT touch required fields
        if (header.required) {
          return header;
        }
        // Toggle all non-required headers
        return { ...header, enable: next };
      });

      return updated;
    });
  };

  const isGroupSelected = (groupKey: string) => {
    const items = allGroups[groupKey] || [];
    if (!items?.length) return false;
    return items?.every((i) => i.enable);
  };

  const handleToggleGroup = (groupKey: string) => {
    const next = !isGroupSelected(groupKey);
    //  if(headers.length>0){
    //   const updated = headers.map((header) =>
    //     getGroupKey(header) === groupKey ? { ...header, enable: next } : header
    //   );
    //   sendHeadersToParent(updated);

    //   return updated;
    // }

    setHeaders((prev) => {
      const updated = prev.map((header) => {
        // Skip if this header is required
        if (header.required) {
          return header;
        }
        // If header belongs to this group → toggle it
        if (getGroupKey(header) === groupKey) {
          return { ...header, enable: next };
        }
        // Otherwise leave as is
        return header;
      });
      return updated;
    });
  };

  useEffect(() => {
    if (activeView !== 'reorder') {
      setReorderMatches([]);
      setActiveMatchIndex(0);
      return;
    }

    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      setReorderMatches([]);
      setActiveMatchIndex(0);
      return;
    }

    const matches = reorderHeaders
      .filter((item) => {
        const v = String(item.value ?? '').toLowerCase();
        const k = String(item.key ?? '').toLowerCase();
        return v.includes(q) || k.includes(q);
      })
      ?.map((item) => String(item.key));

    setReorderMatches(matches);
    setActiveMatchIndex(0);
  }, [searchQuery, reorderHeaders, activeView]);

  const handleNextMatch = () => {
    if (!reorderMatches?.length) return;
    setActiveMatchIndex((prev) => (prev + 1) % reorderMatches?.length);
  };

  const handlePrevMatch = () => {
    if (!reorderMatches?.length) return;
    setActiveMatchIndex(
      (prev) => (prev - 1 + reorderMatches?.length) % reorderMatches?.length
    );
  };

  const highlightedKey =
    activeView === 'reorder' && reorderMatches?.length > 0
      ? (reorderMatches[activeMatchIndex] ?? '')
      : '';

  const handleClickApply = () => {
    sendHeadersToParent(headers);
    //setHeaderList([...headers]);
    // handleLastUsedPresets();
  };

  const totalMatches = reorderMatches?.length;
  const currentMatchNumber =
    totalMatches > 0 ? activeMatchIndex + 1 : undefined;

  return (
    <div
      className={`dwonload-report-pannel ${isShipmentColumns ? 'pt-4' : 'padding-t-0'} `}
    >
      <Flex align='center' justify='space-between'>
        <CustomShipmentText
          text='Column visibility & order'
          fontSize='18px'
          fontWeight={700}
          lineHeight='24px'
        />
      </Flex>

      <Flex vertical>
        <div
          className={`d-panel-tab-toggle ${isShipmentColumns ? 'order-1 margin-t-8px' : 'order-0'} `}
        >
          <button
            type='button'
            className={`report-tabs-button ${isShipmentColumns && 'is-shipment-columns'} ${activeView === 'include' ? 'active' : ''}`}
            onClick={() => setActiveView('include')}
          >
            Included ({includedCount})
          </button>
          <button
            type='button'
            className={`report-tabs-button ${isShipmentColumns && 'is-shipment-columns'} ${activeView === 'reorder' ? 'active' : ''}`}
            onClick={() => setActiveView('reorder')}
          >
            Reorder
          </button>
        </div>

        <div
          className={`relative ${isShipmentColumns ? 'order-0 mt-2' : 'order-1 mt-4'}${activeView === 'reorder' && !isShipmentColumns ? ' report-reorder-search' : ''}`}
        >
          <SearchWithMatchNavigator
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            activeView={activeView === 'reorder'}
            totalMatches={totalMatches}
            currentMatchNumber={currentMatchNumber}
            handlePrevMatch={handlePrevMatch}
            handleNextMatch={handleNextMatch}
          />
        </div>
      </Flex>

      <div className='d-panel-content-scroll'>
        {activeView === 'include' ? (
          <div className='for-reports-filters-selection pt-3'>
            {showSelectAll && (
              <>
                <Flex align='center' justify='space-between' className='mb-1'>
                  <CustomShipmentText
                    text='Select all'
                    fontSize='12px'
                    lineHeight='16px'
                    fontWeight={600}
                    color={
                      isShipmentColumns
                        ? 'var(--text-secondary)'
                        : 'var(--text-primary)'
                    }
                  />
                  <ToggleSwitch
                    labelStyle='alert-customization-bg'
                    checked={isAllSelected}
                    onChange={handleToggleAll}
                  />
                </Flex>

                <Divider className='divider margin-8-12' />
              </>
            )}

            {groupOrder?.map((g) => {
              const items = filteredByGroup[g] || [];
              if (!items?.length) return null;

              const isOpen = !!openGroups[g];
              const groupToggleChecked = isGroupSelected(g);

              return (
                <div key={g} className='mb-3'>
                  <div
                    className={`zl-report-group-row ${isShipmentColumns ? 'isShipment' : ''}`}
                  >
                    <span
                      className='zl-report-group-label'
                      onClick={() => toggleGroup(g)}
                    >
                      {capitalize(groupLabels[g] ?? g)}
                    </span>

                    <Flex
                      justify='flex-end'
                      align='center'
                      gap={6}
                      className='report-group-actions'
                    >
                      <ToggleSwitch
                        labelStyle='alert-customization-bg'
                        checked={groupToggleChecked}
                        onChange={() => handleToggleGroup(g)}
                      />
                      <Flex
                        align='center'
                        className='cursor-pointer'
                        onClick={() => toggleGroup(g)}
                      >
                        {isOpen ? (
                          <ChevronUpIcon size={16} />
                        ) : (
                          <ChevronDownIcon size={16} />
                        )}
                      </Flex>
                    </Flex>
                  </div>

                  {isOpen && (
                    <Flex vertical gap={12} className='pt-1 pb-2 ml-2px'>
                      {items?.map((item) => {
                        const isDisabled = item?.required;
                        return (
                          <Tooltip
                            key={item.key}
                            title={isDisabled ? 'This field is required' : ''}
                          >
                            <Checkbox
                              key={item.key}
                              disabled={isDisabled}
                              checked={item.enable}
                              onChange={(e) => {
                                setHeaders((prev) => {
                                  const updated = prev?.map((header) =>
                                    header.key === item.key
                                      ? { ...header, enable: e.target.checked }
                                      : header
                                  );
                                  return updated;
                                });
                              }}
                            >
                              <TruncateWithTooltip
                                text={capitalize(String(item.value))}
                                max={28}
                                render={(t) => (
                                  <span className='w-fit text-capitalize'>
                                    <CustomShipmentText
                                      text={capitalize(t)}
                                      lineHeight='20px'
                                    />
                                  </span>
                                )}
                              />
                            </Checkbox>
                          </Tooltip>
                        );
                      })}
                    </Flex>
                  )}

                  {!isShipmentColumns && (
                    <Divider className='divider margin-8-12' />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={reorderItemIds}
              strategy={verticalListSortingStrategy}
            >
              {reorderHeaders?.map((item) => {
                if (item?.required) {
                  return null;
                }
                return (
                  <SortableHeaderItem
                    key={item.key}
                    item={item}
                    isHighlighted={item.key === highlightedKey}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        )}
      </div>

      <div className='d-panel-footer-content-shadow' />
      <div className='d-pannel-footer'>
        <Flex justify={isShipmentColumns ? 'flex-end' : 'centre'}>
          <CustomButton
            text='Apply'
            bgColor='var(--text-primary)'
            color='var(--surface-container)'
            fontSize='14px'
            fontWeight='600'
            height='36px'
            borderRadius='8px'
            padding='0 32px'
            width={isShipmentColumns ? '70px' : '100%'}
            onClick={() => {
              handleClickApply();
            }}
          />
        </Flex>
      </div>
    </div>
  );
};

export default ReportPanelContent;

// ---------- Sortable Item ----------

interface SortableHeaderItemProps {
  item: HeaderItem;
  isHighlighted?: boolean;
}

const SortableHeaderItemComponent: FC<SortableHeaderItemProps> = ({
  item,
  isHighlighted,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.key ?? '' });

  const itemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isHighlighted && itemRef.current) {
      itemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, [isHighlighted]);

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        itemRef.current = node;
        if (node) {
          node.style.transform = CSS.Transform.toString(transform) || '';
          node.style.transition = transition || '';
        }
      }}
      className={`report-reorder-item${isDragging ? ' is-dragging' : ''}${isHighlighted ? ' is-highlighted' : ''}`}
    >
      <TruncateWithTooltip
        text={String(item.value)}
        max={32}
        render={(t) => (
          <CustomShipmentText
            text={t}
            lineHeight='20px'
            fontWeight={isHighlighted ? 600 : 500}
            color='var(--text-primary)'
          />
        )}
      />
      <Flex align='center' {...attributes} {...listeners}>
        <DragIcon />
      </Flex>
    </div>
  );
};

const SortableHeaderItem = memo(SortableHeaderItemComponent);
